// Thin API client for Biku chat (onboarding-chat and its feedback lambda).
//
// Backend contract:
//   POST /companies/{id}/onboarding/chat
//     body { message, conversationHistory, routePath, taskId, categoryId, conversationId, companyGroupId }
//     200  { reply, conversationHistory, conversationId, messageId }
//     429  JSON string { error, code: 'user_daily_limit' | 'company_daily_limit' }
//   POST /companies/{id}/onboarding/chat/feedback
//     body { rating, flaggedAnswer, question, conversationHistory, routePath, taskId, companyGroupId, messageId }
//
// Plain async functions (no Vue lifecycle); useBikuChat wraps them with shared state.

import axios from 'axios'
import { Auth } from 'aws-amplify'
// api-config is a CommonJS module (module.exports); pull API_LOCATION off it.
import apiConfig from '@/api-config'

const API_LOCATION: string = (apiConfig as any).API_LOCATION

export interface ChatHistoryEntry {
  role: 'user' | 'assistant'
  content: string
}

export interface ChatReplyResponse {
  reply: string
  conversationHistory: ChatHistoryEntry[]
  conversationId: number | null
  messageId: number | null
}

export type RateLimitCode = 'user_daily_limit' | 'company_daily_limit'

async function authHeader(): Promise<Record<string, string>> {
  const session = await Auth.currentSession()
  return { Authorization: session.getIdToken().getJwtToken() }
}

function companyId(): string | null {
  return localStorage.getItem('companyId')
}

export function currentGroupId(): number | null {
  try {
    return JSON.parse(localStorage.getItem('group') || '{}')?.id ?? null
  } catch (e) {
    return null
  }
}

export async function postChatMessage(body: Record<string, any>): Promise<ChatReplyResponse> {
  const response = await axios.post(`${API_LOCATION}companies/${companyId()}/onboarding/chat`, body, {
    headers: await authHeader(),
    timeout: 30000,
  })
  return response.data
}

export async function postChatFeedback(body: Record<string, any>): Promise<void> {
  await axios.post(`${API_LOCATION}companies/${companyId()}/onboarding/chat/feedback`, body, {
    headers: await authHeader(),
    timeout: 15000,
  })
}

/** 429 bodies from onboarding-chat are JSON with a `code`; anything unparseable counts as the team limit. */
export function rateLimitCode(err: any): RateLimitCode | null {
  if (err?.response?.status !== 429) return null
  try {
    const body = typeof err.response.data === 'string' ? JSON.parse(err.response.data) : err.response.data
    return body?.code === 'user_daily_limit' ? 'user_daily_limit' : 'company_daily_limit'
  } catch (e) {
    return 'company_daily_limit'
  }
}
